import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { TwitterLogo, FacebookLogo, LinkedinLogo, InstagramLogo } from "@phosphor-icons/react";
import { getStatusBadgeClass } from "../config/uiHelpers";

const SocialMedia = ({ user, logout }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSchedule, setShowSchedule] = useState(false);
  const [integrations, setIntegrations] = useState({});
  const [newPost, setNewPost] = useState({
    platform: "twitter",
    content: "",
    scheduled_time: ""
  });

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/social-posts`);
      setPosts(response.data);
    } catch (error) {
      toast.error("Failed to fetch posts");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchIntegrations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/social-posts/integrations`);
      setIntegrations(response.data);
    } catch (error) {
      console.error("Failed to fetch integrations");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchPosts();
    fetchIntegrations();
  }, [fetchPosts, fetchIntegrations]);

  const handleSchedule = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/social-posts`, {
        ...newPost,
        created_by: user.user_id
      });
      toast.success("Post scheduled successfully!");
      setShowSchedule(false);
      setNewPost({ platform: "twitter", content: "", scheduled_time: "" });
      fetchPosts();
    } catch (error) {
      toast.error("Failed to schedule post");
    }
  };

  const getPlatformIcon = (platform) => {
    switch(platform) {
      case 'twitter': return <TwitterLogo size={24} weight="fill" className="text-[#1DA1F2]" />;
      case 'facebook': return <FacebookLogo size={24} weight="fill" className="text-[#1877F2]" />;
      case 'linkedin': return <LinkedinLogo size={24} weight="fill" className="text-[#0A66C2]" />;
      case 'instagram': return <InstagramLogo size={24} weight="fill" className="text-[#E4405F]" />;
      default: return null;
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      
      <div className="flex-1 ml-64">
        <div className="p-8">
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
                Social Media
              </h1>
              <p className="text-base leading-relaxed text-slate-700">
                Schedule and manage social media posts
              </p>
            </div>
            <button
              data-testid="schedule-post-btn"
              onClick={() => setShowSchedule(!showSchedule)}
              className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-medium transition-colors duration-300"
            >
              {showSchedule ? 'Cancel' : 'Schedule Post'}
            </button>
          </div>

          {/* Integration Status */}
          <div data-testid="social-integrations" className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {Object.entries(integrations).map(([platform, info]) => (
              <div key={platform} data-testid={`integration-${platform}`}
                className="bg-white border border-slate-200 rounded-none p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  {getPlatformIcon(platform)}
                  <span className="font-medium capitalize text-sm" style={{fontFamily: 'Outfit, sans-serif'}}>{platform}</span>
                </div>
                <span className={`rounded-none px-2 py-1 text-xs font-bold uppercase tracking-wider border ${
                  info.connected ? 'bg-green-50 text-green-700 border-green-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                }`}>
                  {info.status}
                </span>
              </div>
            ))}
          </div>

          {showSchedule && (
            <div data-testid="schedule-post-form" className="bg-white border border-slate-200 rounded-none p-8 mb-8 shadow-sm">
              <h3 className="text-xl font-semibold mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>Schedule New Post</h3>
              <form onSubmit={handleSchedule} className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Platform</label>
                    <select
                      data-testid="post-platform-select"
                      value={newPost.platform}
                      onChange={(e) => setNewPost({...newPost, platform: e.target.value})}
                      className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none"
                    >
                      <option value="twitter">Twitter</option>
                      <option value="facebook">Facebook</option>
                      <option value="linkedin">LinkedIn</option>
                      <option value="instagram">Instagram</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Schedule Time</label>
                    <input
                      data-testid="post-schedule-time-input"
                      type="datetime-local"
                      value={newPost.scheduled_time}
                      onChange={(e) => setNewPost({...newPost, scheduled_time: e.target.value})}
                      className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Post Content</label>
                  <textarea
                    data-testid="post-content-input"
                    value={newPost.content}
                    onChange={(e) => setNewPost({...newPost, content: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-3 w-full outline-none"
                    rows="5"
                    required
                  />
                </div>
                <button
                  data-testid="submit-post-btn"
                  type="submit"
                  className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-8 py-3 font-medium transition-colors duration-300"
                >
                  Schedule Post
                </button>
              </form>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="loading-spinner"></div>
            </div>
          ) : (
            <div data-testid="posts-list" className="space-y-4">
              {posts.map((post, index) => (
                <div
                  key={post.post_id}
                  data-testid={`post-card-${index}`}
                  className="bg-white border border-slate-200 rounded-none p-6 shadow-sm hover:shadow-md transition-shadow duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-none">
                      {getPlatformIcon(post.platform)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="font-semibold capitalize" style={{fontFamily: 'Outfit, sans-serif'}}>
                          {post.platform}
                        </span>
                        <span className={`rounded-none px-2 py-1 text-xs font-bold uppercase tracking-wider border ${getStatusBadgeClass(post.status, "social")}`}>
                          {post.status}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 mb-3">{post.content}</p>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-slate-500">
                          Scheduled: {new Date(post.scheduled_time).toLocaleString()}
                        </p>
                        {post.status === 'scheduled' && (
                          <button data-testid={`publish-post-${index}`}
                            onClick={async () => {
                              try {
                                const res = await axios.post(`${API}/social-posts/${post.post_id}/publish`);
                                toast.success(res.data.message);
                                fetchPosts();
                              } catch (err) { toast.error("Publish failed"); }
                            }}
                            className="rounded-none bg-[#10B981] text-white hover:bg-[#059669] px-4 py-2 text-xs font-bold transition-colors duration-300">
                            Publish Now
                          </button>
                        )}
                      </div>
                      {post.publish_result && (
                        <p className="text-xs text-slate-500 mt-2 italic">{post.publish_result}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocialMedia;
